import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const FeatureCard = ({ item, index }) => {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    margin: "-80px",
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{
        transform: "translate3d(0,0,0)",
        willChange: "transform, opacity",
      }}
    >
      {/* Static Card */}
      <div
        className="group relative p-10 rounded-[32px] bg-white/[0.03] border border-white/5
        transition-all duration-300 hover:bg-white/[0.06] hover:border-blue-400/30 hover:-translate-y-1"
      >
        {/* Number */}
        <div className="absolute top-11 sm:top-8 right-8 text-3xl font-black text-blue-500/10 group-hover:text-blue-500/20 transition-colors">
          {item.num}
        </div>

        {/* Icon */}
        <div
          className="text-4xl md:text-5xl mb-6 sm:mb-8 transition-transform duration-500 group-hover:scale-105 group-hover:rotate-6"
          style={{
            transform: "translate3d(0,0,0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {item.icon}
        </div>

        {/* Title */}
        <h4 className="text-white font-black text-xl mb-3 tracking-tight">
          {item.title}
        </h4>

        {/* Description */}
        <p className="text-white/50 text-sm leading-relaxed font-bold">
          {item.desc}
        </p>
      </div>
    </motion.div>
  );
};

const WhyUsSection = () => {
  const features = [
    {
      num: "01",
      icon: "🖨️",
      title: "Modern Printing Machinery",
      desc: "Latest digital & flexo presses delivering consistent color accuracy and sharp detail at high speeds.",
    },
    {
      num: "02",
      icon: "✅",
      title: "Rigorous Quality Control",
      desc: "Multi-stage QC process with spectrophotometer color checks and automated inspection before dispatch.",
    },
    {
      num: "03",
      icon: "🚀",
      title: "Fast Turnaround",
      desc: "Standard 5–7 day lead time. Express 24–48 hour production for urgent requirements.",
    },
    {
      num: "04",
      icon: "💰",
      title: "Competitive Pricing",
      desc: "Volume-based pricing with transparent quotes. No hidden charges, no minimum order surprises.",
    },
    {
      num: "05",
      icon: "🎨",
      title: "Expert Design Support",
      desc: "In-house graphic designers to help you create or refine artwork ready for print-perfect output.",
    },
    {
      num: "06",
      icon: "🌐",
      title: "Pan-India & Global Export",
      desc: "Reliable logistics network ensuring timely delivery across India and international shipping for global clients.",
    },
  ];

  return (
    <section
      id="why-us"
      className="py-20 sm:py-24 bg-[#0a1628] overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="text-blue-400 font-black text-xs mb-6 bg-blue-500/5 px-5 py-2 border border-blue-400/20 rounded-full w-fit uppercase mx-auto tracking-[0.2em]">
            Why Choose Sahajink
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">
            The Sahajink Advantage
          </h2>

          <p className="text-white/40 max-w-[650px] mx-auto text-[16px] sm:text-lg">
            We go beyond just printing. We are a quality-obsessed,
            technology-driven label partner committed to your success.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((item, i) => (
            <FeatureCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
